import { icons, type LucideIcon } from 'lucide-react';

import type { ChallengeIconType } from '@/lib/eventBranding';

/**
 * The full lucide catalog, keyed by the PascalCase component name — the exact
 * string stored in `challengeTypeIcons[type].icon`. Storing the catalog key
 * (rather than a kebab-case slug) avoids lossy name conversions ("Grid3x3"
 * has no unambiguous kebab round-trip) and lets mobile resolve the same name
 * straight off the `lucide-react-native` module namespace. Unknown names fall
 * back to the type's built-in icon on every surface.
 */
export const MAP_ICON_REGISTRY: Record<string, LucideIcon> = icons;

/** Every pickable icon name, alphabetical (lucide ships them sorted). */
export const ALL_MAP_ICON_NAMES: string[] = Object.keys(icons);

/** Registry lookup that tolerates null/undefined/unknown names. */
export function resolveMapIcon(name: string | null | undefined): LucideIcon | null {
  return (name && MAP_ICON_REGISTRY[name]) || null;
}

/** "TreePine" → "Tree Pine" for search + tooltips. */
export function formatIconName(name: string): string {
  return name.replace(/([a-z])([A-Z])/g, '$1 $2');
}

/**
 * The built-in icon + pin color each challenge type uses when no override is
 * set — what the branding editor shows as "the default". Mirrors mobile's
 * `typeIcons` (ChallengePin) and `MAP_ICON_COLORS_BY_TYPE` — keep in sync.
 */
export const CHALLENGE_TYPE_MAP_DEFAULTS: Record<
  ChallengeIconType,
  { icon: string; color: string }
> = {
  riddle: { icon: 'HelpCircle', color: '#F97316' },
  counting: { icon: 'Hash', color: '#2563EB' },
  exploration: { icon: 'Compass', color: '#16A34A' },
  search: { icon: 'Search', color: '#16A34A' },
  observation: { icon: 'Eye', color: '#16A34A' },
  photo: { icon: 'Camera', color: '#EC4899' },
  video: { icon: 'Video', color: '#15803D' },
  finale: { icon: 'Trophy', color: '#EAB308' },
  puzzle: { icon: 'Puzzle', color: '#8B5CF6' },
};
